<script setup>
import { Head } from '@inertiajs/vue3';
import AuthenticationCardLogo from '@/Components/AuthenticationCardLogo.vue';

defineProps({
    terms: String,
});
</script>

<template>
    <Head title="Algemene Voorwaarden – besapro" />

    <div class="font-sans text-gray-900 antialiased">
        <div class="pt-4 bg-gray-100">
            <div class="min-h-screen flex flex-col items-center pt-6 sm:pt-0">
                <div>
                    <AuthenticationCardLogo />
                </div>

                <div
                    class="w-full sm:max-w-2xl mt-6 p-6 bg-white shadow-md overflow-hidden sm:rounded-lg prose"
                >
                    <h1>Algemene Voorwaarden – besapro</h1>
                    <p>Laatst bijgewerkt: 6 augustus 2025</p>

                    <!-- ──────────────────────────────────── -->
                    <!-- 1. Definities & identiteit            -->
                    <!-- ──────────────────────────────────── -->
                    <h2>Definities</h2>
                    <ul>
                        <li><strong>Besapro Platform</strong> (hierna: “Besapro”): het online platform dat bestaat uit de modules <em>Sync</em> en <em>Timesheets</em>.</li>
                        <li><strong>Sync</strong>: module waarmee gegevens tussen Teamleader en andere systemen in (near-)realtime gesynchroniseerd worden. Data wordt alleen tijdelijk verwerkt en niet structureel opgeslagen.</li>
                        <li><strong>Timesheets</strong>: module waarmee gewerkte uren worden geregistreerd en gefactureerd.</li>
                        <li>
                            <strong>Bedrijf</strong>: Pro-fase BV, Gasthuisstraat 11, 1785 Merchtem, België<br>
                            BTW-nr: BE1000.844.911<br>
                            E-mail: <a href="mailto:info@besapro.eu">info@besapro.eu</a> • Tel.: <span class="whitespace-nowrap">+32 486 92 63 21</span>
                        </li>
                        <li><strong>Dienst</strong>: het Besapro Platform (inclusief alle bijbehorende websites, mobiele- en desktop-applicaties) en de daarmee samenhangende diensten.</li>
                        <li><strong>Gebruiker / U</strong>: iedere rechtspersoon die een account aanmaakt of anderszins gebruikmaakt van de Dienst voor beroeps- of bedrijfsdoeleinden.</li>
                        <li><strong>Abonnement</strong>: een betaald, doorlopend contract dat toegang geeft tot (onderdelen van) de Dienst.</li>
                        <li><strong>Verwerking</strong>: elke bewerking met betrekking tot persoonsgegevens in de zin van de AVG.</li>
                    </ul>

                    <!-- ──────────────────────────────────── -->
                    <h2>Toepasselijkheid</h2>
                    <p>
                        Deze Algemene Voorwaarden zijn van toepassing op elk aanbod van het Bedrijf en op iedere
                        overeenkomst of rechtsverhouding tussen het Bedrijf en de Gebruiker met betrekking tot de Dienst.
                        Door de Dienst te gebruiken accepteert u deze voorwaarden. De Gebruiker verklaart uitsluitend als
                        ondernemer te handelen; consumenten-rechten zijn niet van toepassing.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>Modulespecifieke bepalingen</h2>
                    <h3>Sync</h3>
                    <ul>
                        <li>
                            Het Bedrijf treedt op als <strong>verwerker</strong> voor de gegevens die via Sync worden
                            doorgestuurd. Gegevens worden alleen tijdelijk gebufferd voor technische levering en
                            worden niet opgeslagen nadat de synchronisatie is voltooid.
                        </li>
                        <li>
                            De Gebruiker blijft <strong>verwerkingsverantwoordelijke</strong> in de zin van de AVG voor alle met
                            Sync verwerkte data en garandeert een geldige verwerkingsgrondslag.
                        </li>
                    </ul>

                    <h3>Timesheets</h3>
                    <ul>
                        <li>
                            Timesheets slaat urenregistraties, klanten- en projectdata op in de door het Bedrijf beheerde cloud-
                            omgeving binnen de EU. De gegevens worden uitsluitend gebruikt om gewerkte uren te
                            registreren, rapporteren en factureren.
                        </li>
                        <li>
                            Voor deze verwerkingen is het Bedrijf <strong>verwerkingsverantwoordelijke</strong>.
                            Het volledige privacy- en beveiligingsbeleid is beschreven in de Privacyverklaring.
                        </li>
                    </ul>

                    <!-- ──────────────────────────────────── -->
                    <h2>Abonnementen &amp; Betalingen</h2>
                    <h3>Looptijd &amp; Verlenging</h3>
                    <p>
                        Abonnementen worden aangegaan voor een initiële contractduur van één (1) jaar en
                        worden daarna stilzwijgend verlengd voor opeenvolgende periodes van één (1) jaar.
                        Opzegging is mogelijk tegen het einde van de lopende periode met een opzegtermijn van
                        één (1) maand, via de account-portal of per e-mail.
                    </p>

                    <h3>Prijzen &amp; btw</h3>
                    <p>Alle prijzen zijn in euro’s en exclusief btw, tenzij uitdrukkelijk anders vermeld.</p>

                    <h3>Facturatie &amp; Betaling</h3>
                    <p>
                        Facturen worden elektronisch verstrekt en dienen binnen
                        veertien (14) kalenderdagen te worden voldaan.
                        Bij overschrijding van de vervaldatum is zonder ingebrekestelling een rente
                        verschuldigd conform de Wet Betalingsachterstand Handelstransacties.
                    </p>

                    <h3>Tarievenwijziging</h3>
                    <p>
                        Het Bedrijf kan tarieven aanpassen. De Gebruiker wordt hiervan minimaal
                        dertig (30) dagen vooraf elektronisch op de hoogte gesteld. Indien u de aangepaste
                        tarieven niet accepteert, kunt u het Abonnement opzeggen met inachtneming van de
                        opzegtermijn.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>Intellectuele Eigendomsrechten &amp; Gebruik</h2>
                    <p>
                        Alle intellectuele eigendomsrechten op (onderdelen van) de Dienst berusten bij het Bedrijf
                        of diens licentiegevers. De Gebruiker verkrijgt een niet-exclusieve, niet-overdraagbare en
                        herroepbare licentie om de Dienst gedurende de abonnementsperiode te gebruiken voor
                        interne bedrijfsdoeleinden. Reverse-engineering, herverkoop of het ter beschikking stellen
                        aan derden is niet toegestaan zonder voorafgaande schriftelijke toestemming.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>Acceptabel Gebruik &amp; Opschorting</h2>
                    <p>
                        U mag de Dienst niet gebruiken voor illegale activiteiten, het verzenden van malware,
                        buitensporig verbruik van resources of inbreuk op rechten van derden.
                        Bij overtreding kan het Bedrijf de Dienst onmiddellijk opschorten. Gegevens blijven
                        maximaal dertig (30) dagen beschikbaar voor export, tenzij de wet anders vereist.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>Beveiliging, Data-lokalisatie &amp; Incidentmelding</h2>
                    <p>
                        Het Bedrijf past passende technische en organisatorische maatregelen toe, waaronder
                        encryptie in transit, 2-factor-authenticatie en rol-gebaseerde toegangscontrole.
                        Subverwerkers worden uitsluitend ingeschakeld op basis van een adequaatheidsbesluit of
                        Standard Contractual Clauses indien zij buiten de EER gevestigd zijn.
                        Het Bedrijf meldt een inbreuk op persoonsgegevens zonder onredelijke vertraging nadat
                        het hiervan kennis heeft genomen.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>Force Majeure</h2>
                    <p>
                        Geen van beide partijen is gehouden tot nakoming van verplichtingen die onmogelijk
                        worden door omstandigheden buiten redelijke controle, waaronder maar niet beperkt tot
                        natuurrampen, oorlog, stakingen, pandemieën, storingen in openbare infrastructuur of
                        DDoS-aanvallen.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>Aansprakelijkheid</h2>
                    <p>
                        De totale contractuele en buiten-contractuele aansprakelijkheid van het Bedrijf
                        is beperkt tot het bedrag dat de Gebruiker in de twaalf (12) maanden voorafgaand aan het
                        schade-veroorzakende incident voor de Dienst heeft betaald.
                        Deze beperking geldt niet in geval van opzet of grove nalatigheid van het Bedrijf
                        en laat dwingendrechtelijke aansprakelijkheidsregels onverlet.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>Wijzigingen van deze Voorwaarden</h2>
                    <p>
                        Het Bedrijf kan deze Voorwaarden wijzigen. Wijzigingen worden ten minste
                        dertig (30) dagen vooraf aangekondigd via e-mail en/of het platform.
                        Blijft u de Dienst gebruiken na inwerkingtreding, dan wordt dit beschouwd als acceptatie.
                        U kunt in dat geval het Abonnement beëindigen conform de opzegprocedure.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>Toepasselijk Recht &amp; Forum</h2>
                    <p>
                        Op deze Voorwaarden is uitsluitend Belgisch recht van toepassing.
                        Geschillen worden voorgelegd aan de bevoegde rechtbanken van Belgie,
                        onverminderd het recht van het Bedrijf een procedure aanhangig te maken
                        in het rechtsgebied van de statutaire zetel van de Gebruiker.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>Contact</h2>
                    <p>
                        Vragen over deze Voorwaarden kunnen gericht worden aan
                        <a href="mailto:info@besapro.eu">info@besapro.eu</a>.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
